import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CurrencyDto, HttpService } from 'src/app/@services/http/http.service';

export interface AppCurrencySettings {
  code: string;
  symbol: string;
  name: string;
}

const DEFAULT_CURRENCY: AppCurrencySettings = {
  code: 'USD',
  symbol: '$',
  name: 'US Dollar'
};

/**
 * Single money formatter for CMS: always 2 decimal places + thousand separators.
 * Display uses Currency Setting: `SYMBOL + amount + CODE` (e.g. `$1,000.00 GYD`).
 * Per-row API currency fields are ignored so the CMS stays consistent.
 */
@Injectable({ providedIn: 'root' })
export class AppCurrencyService {
  private readonly settingsSubject = new BehaviorSubject<AppCurrencySettings>({ ...DEFAULT_CURRENCY });
  readonly settings$ = this.settingsSubject.asObservable();
  private loaded = false;
  private loading = false;

  constructor(private http: HttpService) {}

  get settings(): AppCurrencySettings {
    return this.settingsSubject.value;
  }

  /** ISO code saved in Currency Setting (e.g. GYD, USD). */
  get code(): string {
    return (this.settings.code || DEFAULT_CURRENCY.code).toUpperCase();
  }

  ensureLoaded(): void {
    if (this.loaded || this.loading) {
      return;
    }
    this.reload();
  }

  reload(): void {
    this.loading = true;
    this.http.getCurrency().subscribe({
      next: (res) => {
        this.applyDto(res?.data);
        this.loaded = true;
        this.loading = false;
      },
      error: () => {
        this.settingsSubject.next({ ...DEFAULT_CURRENCY });
        this.loaded = true;
        this.loading = false;
      }
    });
  }

  applyLocal(partial: Partial<AppCurrencySettings>): void {
    this.settingsSubject.next({
      ...this.settingsSubject.value,
      ...partial
    });
    this.loaded = true;
  }

  /**
   * Format a monetary amount using Currency Setting symbol + code.
   * Display shape: `$1,000.00 GYD` (symbol + amount + ISO code).
   * @param value numeric amount
   * @param _currency ignored — kept for call-site compatibility; display always uses settings
   */
  format(value: unknown, _currency?: string | null, empty = '—'): string {
    if (value === null || value === undefined || value === '') {
      return this.withSystemCurrency('0.00');
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
      return empty;
    }
    const absolute = Math.abs(n);
    const formatted = absolute.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const signed = n < 0 ? `-${formatted}` : formatted;
    return this.withSystemCurrency(signed);
  }

  /** e.g. `$1,234.56 GYD` from Currency Setting symbol + code. */
  private withSystemCurrency(amountText: string): string {
    const code = this.code;
    const symbol = String(this.settings.symbol || '').trim() || this.fallbackSymbol(code) || '';
    if (symbol) {
      return `${symbol}${amountText} ${code}`;
    }
    return `${amountText} ${code}`;
  }

  private applyDto(dto?: CurrencyDto | null): void {
    if (!dto) {
      this.settingsSubject.next({ ...DEFAULT_CURRENCY });
      return;
    }
    const code = String(dto.short_name || DEFAULT_CURRENCY.code).trim().toUpperCase() || DEFAULT_CURRENCY.code;
    const symbol = String(dto.symbol || '').trim();
    const name = String(dto.full_name || '').trim() || DEFAULT_CURRENCY.name;
    this.settingsSubject.next({
      code,
      symbol: symbol || this.fallbackSymbol(code),
      name
    });
  }

  private fallbackSymbol(code: string): string {
    switch (code) {
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      case 'INR':
        return '₹';
      case 'GYD':
        return 'G$';
      default:
        return '';
    }
  }
}
